import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, FileWarning, Globe, ClipboardList, Eye } from 'lucide-react';
import { AriaLogo } from '../components/AriaLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { UrgencyBadge, EmergencyFlagBadge } from '../components/UrgencyBadge';
import { ImageViewer } from '../components/ImageViewer';
import { AnalysisReport } from '../components/AnalysisReport';
import { getLesionMarkers } from './AnalysisDetailPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedAnalysisPage = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/shared/${token}`)
      .then((res) => setAnalysis(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(210,25%,6%)] flex items-center justify-center">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (notFound || !analysis) {
    return (
      <div className="min-h-screen bg-[hsl(210,25%,6%)] flex flex-col items-center justify-center gap-4">
        <FileWarning size={48} className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t('shared.not_found')}</p>
      </div>
    );
  }

  const report = analysis.report_json || {};
  const level = report.emergency_level || 'MODERATE';
  const flag = report.emergency_flag || 'UNCERTAIN';
  const markers = getLesionMarkers(report);
  const images = analysis.images?.length
    ? analysis.images
    : [{ image_base64: analysis.image_base64, image_mime: analysis.image_mime }];

  return (
    <div className="min-h-screen bg-[hsl(210,25%,6%)] grain-overlay" data-testid="shared-analysis-page">
      {/* Minimal header */}
      <header className="h-16 bg-[hsl(210,18%,7%)]/90 backdrop-blur-md border-b border-border">
        <div className="h-full max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AriaLogo size={36} className="shrink-0" />
            <div>
              <h1 className="text-sm font-semibold text-foreground leading-none">{t('app.title')}</h1>
              <p className="text-[10px] text-muted-foreground mt-0.5">{t('shared.title')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-purple-300 bg-purple-500/15 px-2.5 py-1 rounded-full ring-1 ring-purple-500/30">
              <Eye size={11} />
              {t('shared.readonly')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
              data-testid="shared-language-toggle"
            >
              <Globe size={16} />
              <span className="text-xs font-semibold uppercase">{i18n.language}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
        {/* Summary strip */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <UrgencyBadge level={level} size="lg" />
          <EmergencyFlagBadge flag={flag} />
          <span className="text-xs text-muted-foreground">
            {report.modality_guess} | {report.region}
            {analysis.user_name ? ` · ${t('report.analyzed_by')}: ${analysis.user_name}` : ''}
          </span>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-12 xl:col-span-5">
            <Card className="bg-[hsl(210,18%,9%)] border-border overflow-hidden">
              <CardContent className="p-0">
                <ImageViewer
                  images={images}
                  priorImages={analysis.prior_images || []}
                  markers={markers}
                  annotations={analysis.user_annotations || []}
                  readOnly
                  t={t}
                />
              </CardContent>
            </Card>

            {analysis.clinical_context && (
              <Card className="mt-4 bg-[hsl(210,18%,9%)] border-border">
                <CardContent className="py-4">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                    {t('context.title')}
                  </p>
                  <p className="text-xs text-foreground/80">{analysis.clinical_context}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="col-span-12 xl:col-span-7">
            <Card className="bg-[hsl(210,18%,9%)] border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList size={20} className="text-cyan-400" />
                  {t('report.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AnalysisReport report={report} t={t} />
              </CardContent>
            </Card>
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground text-center mt-8">
          {t('disclaimer_banner')}
        </p>
      </main>
    </div>
  );
};

export default SharedAnalysisPage;
